import React from 'react';
import { alpha } from '@mui/material/styles';

// Material UI Components
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Grid,
  Box,
  Avatar,
  Chip,
  Divider,
  Paper,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  useTheme
} from '@mui/material';

// Material UI Icons
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  Lightbulb as LightbulbIcon,
  Flag as GoalsIcon,
  Visibility as PhysicalIcon,
  History as HistoryIcon,
  Warning as ConflictIcon,
  Timeline as ArcIcon,
  RecordVoiceOver as VoiceIcon,
  People as RelationshipsIcon,
  CheckCircleOutline as StrengthsWeaknessesIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  ArrowRight as ArrowRightIcon,
  Mood as PersonalityIcon
} from '@mui/icons-material';

// Character data constants
// ==============================================

// Character role options with descriptions
const characterRoles = [
  { value: 'protagonist', label: 'Protagonist' },
  { value: 'antagonist', label: 'Antagonist' },
  { value: 'mentor', label: 'Mentor' },
  { value: 'ally', label: 'Ally' },
  { value: 'sidekick', label: 'Sidekick' },
  { value: 'love-interest', label: 'Love Interest' },
  { value: 'foil', label: 'Foil' },
  { value: 'catalyst', label: 'Catalyst' }
];

// Character archetype options with descriptions
const characterArchetypes = [
  { value: 'hero', label: 'Hero' },
  { value: 'shadow', label: 'Shadow' },
  { value: 'mentor', label: 'Mentor' },
  { value: 'threshold-guardian', label: 'Threshold Guardian' },
  { value: 'herald', label: 'Herald' },
  { value: 'shapeshifter', label: 'Shapeshifter' },
  { value: 'trickster', label: 'Trickster' },
  { value: 'ally', label: 'Ally' }
];

// Myers-Briggs personality types with descriptions
const personalityTypes = [
  { value: 'INTJ', label: 'INTJ - The Architect' },
  { value: 'INTP', label: 'INTP - The Logician' },
  { value: 'ENTJ', label: 'ENTJ - The Commander' },
  { value: 'ENTP', label: 'ENTP - The Debater' },
  { value: 'INFJ', label: 'INFJ - The Advocate' },
  { value: 'INFP', label: 'INFP - The Mediator' },
  { value: 'ENFJ', label: 'ENFJ - The Protagonist' },
  { value: 'ENFP', label: 'ENFP - The Campaigner' },
  { value: 'ISTJ', label: 'ISTJ - The Logistician' },
  { value: 'ISFJ', label: 'ISFJ - The Defender' },
  { value: 'ESTJ', label: 'ESTJ - The Executive' },
  { value: 'ESFJ', label: 'ESFJ - The Consul' },
  { value: 'ISTP', label: 'ISTP - The Virtuoso' },
  { value: 'ISFP', label: 'ISFP - The Adventurer' },
  { value: 'ESTP', label: 'ESTP - The Entrepreneur' },
  { value: 'ESFP', label: 'ESFP - The Entertainer' }
];

const CharacterDetailView = ({ 
  open, 
  onClose, 
  character, 
  onEdit, 
  relationships = [], 
  getCharacterNameById = () => 'Unknown Character'
}) => {
  const theme = useTheme();
  // Early return with proper error handling
  if (!open) return null;
  
  if (!character) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5">Character Details</Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Character information not available
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const getRelationshipTypeLabel = (type) => {
    const types = {
      'friend': 'Friend',
      'enemy': 'Enemy',
      'family': 'Family',
      'lover': 'Lover',
      'mentor': 'Mentor',
      'colleague': 'Colleague',
      'rival': 'Rival',
      'acquaintance': 'Acquaintance'
    };
    return types[type] || type;
  };

  const getRoleLabel = (value) => {
    const role = characterRoles.find(r => r.value === value);
    return role ? role.label : value;
  };

  const getArchetypeLabel = (value) => {
    const archetype = characterArchetypes.find(a => a.value === value);
    return archetype ? archetype.label : value;
  };

  const getPersonalityLabel = (value) => {
    const type = personalityTypes.find(t => t.value === value);
    return type ? type.label : value;
  };

  const normalizeTraitList = (items = []) => {
    return items.map((item) => {
      if (typeof item === 'string') {
        return { label: item, description: '' };
      }
      if (item && typeof item === 'object') {
        return {
          label: item.name || item.label || item.value || '',
          description: item.description || item.detail || ''
        };
      }
      return { label: String(item || ''), description: '' };
    }).filter((t) => t.label);
  };

  const characterRelationships = relationships.filter(
    r => r.sourceId === character.id || r.targetId === character.id
  );
  const strengthsList = normalizeTraitList(character.strengths || []);
  const weaknessesList = normalizeTraitList(character.weaknesses || []);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: theme.palette.mode === 'dark' ? alpha('#0a0a0a', 0.95) : '#fff',
          backdropFilter: 'blur(20px)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 12px 48px rgba(0, 0, 0, 0.7)'
            : '0 12px 48px rgba(0, 0, 0, 0.15)',
          border: theme.palette.mode === 'dark'
            ? `1px solid ${alpha(theme.palette.divider, 0.15)}`
            : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          height: { xs: '100%', sm: 'auto' },
          maxHeight: { xs: '100%', sm: '90vh' }
        }
      }}
    >
      {character && (
        <>
          <DialogTitle 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 3,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                }}
              >
                <PersonIcon sx={{ color: 'white', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                  {character.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                  {character.role && (
                    <Chip 
                      label={getRoleLabel(character.role)} 
                      size="small" 
                      sx={{ 
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        border: '1px solid'
                      }} 
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{ color: (theme) => theme.palette.text.secondary }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              {/* Left Column */}
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  mb: 3,
                  mt: 2
                }}>
                  <Box sx={{ position: 'relative', mb: 4 }}>
                    <Box
                      sx={{
                        width: 160,
                        height: 200,
                        borderRadius: 1,
                        overflow: 'hidden',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 12px 32px rgba(0,0,0,0.5)'
                          : '0 8px 24px rgba(0,0,0,0.15)',
                        border: `4px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 16px 40px rgba(0,0,0,0.6)'
                            : '0 12px 32px rgba(0,0,0,0.2)'
                        }
                      }}
                    >
                      {character.imageUrl ? (
                        <Box
                          component="img"
                          src={character.imageUrl}
                          alt={character.name}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <PersonIcon sx={{ fontSize: 80, color: 'white' }} />
                      )}
                    </Box>
                    {/* Decorative ring */}
                    <Box sx={{
                      position: 'absolute',
                      top: -8,
                      left: -8,
                      right: -8,
                      bottom: -8,
                      borderRadius: 1,
                      border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      pointerEvents: 'none'
                    }} />
                  </Box>
                  
                  <Box sx={{ width: '100%' }}>
                    <Paper elevation={0} sx={{ 
                      p: 2.5, 
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha('#1a1a1a', 0.8)
                        : alpha(theme.palette.grey[50], 0.5),
                      mb: 2,
                      borderRadius: 1,
                      border: theme.palette.mode === 'dark'
                        ? `1px solid ${alpha(theme.palette.divider, 0.25)}`
                        : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 12px rgba(0,0,0,0.4)'
                        : '0 2px 8px rgba(0,0,0,0.06)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha('#222', 0.85)
                          : alpha(theme.palette.grey[50], 0.7),
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 6px 16px rgba(0,0,0,0.5)'
                          : '0 4px 12px rgba(0,0,0,0.08)'
                      }
                    }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                        pb: 1.5,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
                      }}>
                        <Box sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(theme.palette.primary.main, 0.1)})`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5
                        }}>
                          <PersonIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                        </Box>
                        <Typography variant="subtitle2" sx={{ 
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          textTransform: 'uppercase',
                          fontSize: '0.75rem'
                        }}>
                          Basic Information
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        {character.age && (
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Age</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.mode === 'dark' ? alpha('#fff', 0.95) : 'text.primary' }}>{character.age}</Typography>
                          </Grid>
                        )}
                        {character.occupation && (
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Occupation</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.mode === 'dark' ? alpha('#fff', 0.95) : 'text.primary' }}>{character.occupation}</Typography>
                          </Grid>
                        )}
                        {(!character.age && !character.occupation) && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              No basic information provided.
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                    
                    <Paper elevation={0} sx={{ 
                      p: 2.5, 
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha('#1a1a1a', 0.8)
                        : alpha(theme.palette.grey[50], 0.5),
                      mb: 2,
                      borderRadius: 1,
                      border: theme.palette.mode === 'dark'
                        ? `1px solid ${alpha(theme.palette.divider, 0.25)}`
                        : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 12px rgba(0,0,0,0.4)'
                        : '0 2px 8px rgba(0,0,0,0.06)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha('#222', 0.85)
                          : alpha(theme.palette.grey[50], 0.7),
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 6px 16px rgba(0,0,0,0.5)'
                          : '0 4px 12px rgba(0,0,0,0.08)'
                      }
                    }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                        pb: 1.5,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
                      }}>
                        <Box sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.2)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5
                        }}>
                          <PsychologyIcon sx={{ color: 'secondary.main', fontSize: 18 }} />
                        </Box>
                        <Typography variant="subtitle2" sx={{ 
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          textTransform: 'uppercase',
                          fontSize: '0.75rem'
                        }}>
                          Role & Archetype
                        </Typography>
                      </Box>
                      {character.role && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Role</Typography>
                          <Chip 
                            label={getRoleLabel(character.role)} 
                            size="medium" 
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              backgroundColor: alpha(theme.palette.primary.main, 0.15),
                              color: theme.palette.primary.main,
                              border: `1.5px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.25)
                              }
                            }} 
                          />
                        </Box>
                      )}
                      {character.archetype && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Archetype</Typography>
                          <Chip 
                            label={getArchetypeLabel(character.archetype)} 
                            size="medium" 
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              backgroundColor: alpha(theme.palette.secondary.main, 0.15),
                              color: theme.palette.secondary.main,
                              border: `1.5px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.secondary.main, 0.25)
                              }
                            }} 
                          />
                        </Box>
                      )}
                    </Paper>
                    
                    {character.personalityType && (
                      <Paper elevation={0} sx={{ 
                        p: 2.5, 
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha('#1a1a1a', 0.8)
                          : alpha(theme.palette.grey[50], 0.5),
                        mb: 2,
                        borderRadius: 1,
                        border: theme.palette.mode === 'dark'
                          ? `1px solid ${alpha(theme.palette.divider, 0.25)}`
                          : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 4px 12px rgba(0,0,0,0.4)'
                          : '0 2px 8px rgba(0,0,0,0.06)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha('#222', 0.85)
                            : alpha(theme.palette.grey[50], 0.7),
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 6px 16px rgba(0,0,0,0.5)'
                            : '0 4px 12px rgba(0,0,0,0.08)'
                        }
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 2,
                          pb: 1.5,
                          borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
                        }}>
                          <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.2)}, ${alpha(theme.palette.info.main, 0.1)})`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 1.5
                          }}>
                            <PersonalityIcon sx={{ color: 'info.main', fontSize: 18 }} />
                          </Box>
                          <Typography variant="subtitle2" sx={{ 
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                            fontSize: '0.75rem'
                          }}>
                            Personality Type
                          </Typography>
                        </Box>
                        <Chip 
                          label={getPersonalityLabel(character.personalityType)} 
                          size="medium" 
                          sx={{ 
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            backgroundColor: alpha(theme.palette.info.main, 0.15),
                            color: theme.palette.info.main,
                            border: `1.5px solid ${alpha(theme.palette.info.main, 0.3)}`,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.info.main, 0.25)
                            }
                          }} 
                        />
                      </Paper>
                    )}
                  </Box>
                </Box>
              </Grid>
              
              {/* Right Column */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  {/* Strengths & Weaknesses */}
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ 
                      p: 2.5, 
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha('#1a1a1a', 0.8)
                        : alpha(theme.palette.grey[50], 0.5),
                      mb: 2,
                      borderRadius: 1,
                      border: theme.palette.mode === 'dark'
                        ? `1px solid ${alpha(theme.palette.divider, 0.25)}`
                        : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 12px rgba(0,0,0,0.4)'
                        : '0 2px 8px rgba(0,0,0,0.06)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha('#222', 0.85)
                          : alpha(theme.palette.grey[50], 0.7),
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 6px 16px rgba(0,0,0,0.5)'
                          : '0 4px 12px rgba(0,0,0,0.08)'
                      }
                    }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2.5,
                        pb: 1.5,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
                      }}>
                        <Box sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1.5,
                          background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.2)}, ${alpha(theme.palette.success.main, 0.1)})`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5
                        }}>
                          <StrengthsWeaknessesIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        </Box>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 700,
                          letterSpacing: '-0.3px'
                        }}>
                          Strengths & Weaknesses
                        </Typography>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom sx={{ 
                            fontWeight: 600,
                            color: 'success.main',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            <CheckCircleOutlineIcon sx={{ mr: 0.5, fontSize: 18 }} />
                            Strengths
                          </Typography>
                          
                          {strengthsList.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                              {strengthsList.map((strength, index) => {
                                const chip = (
                                  <Chip
                                    key={`${strength.label}-${index}`}
                                    label={strength.label}
                                    variant="outlined"
                                    color="success"
                                    sx={{
                                      borderRadius: 1,
                                      fontWeight: 600,
                                      background: alpha(theme.palette.success.main, 0.05),
                                      borderColor: alpha(theme.palette.success.main, 0.4)
                                    }}
                                  />
                                );
                                return strength.description ? (
                                  <Tooltip key={`${strength.label}-${index}`} title={strength.description} placement="top">
                                    {chip}
                                  </Tooltip>
                                ) : chip;
                              })}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', ml: 3 }}>
                              No strengths defined.
                            </Typography>
                          )}
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom sx={{ 
                            fontWeight: 600,
                            color: 'error.main',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            <ErrorOutlineIcon sx={{ mr: 0.5, fontSize: 18 }} />
                            Weaknesses
                          </Typography>
                          
                          {weaknessesList.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                              {weaknessesList.map((weakness, index) => {
                                const chip = (
                                  <Chip
                                    key={`${weakness.label}-${index}`}
                                    label={weakness.label}
                                    variant="outlined"
                                    color="error"
                                    sx={{
                                      borderRadius: 1,
                                      fontWeight: 600,
                                      background: alpha(theme.palette.error.main, 0.04),
                                      borderColor: alpha(theme.palette.error.main, 0.35)
                                    }}
                                  />
                                );
                                return weakness.description ? (
                                  <Tooltip key={`${weakness.label}-${index}`} title={weakness.description} placement="top">
                                    {chip}
                                  </Tooltip>
                                ) : chip;
                              })}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', ml: 3 }}>
                              No weaknesses defined.
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                  
                  {/* Physical Description */}
                  {character.physicalDescription && (
                    <Grid item xs={12}>
                      <Paper elevation={0} sx={{ 
                        p: 2.5, 
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha('#1a1a1a', 0.8)
                          : alpha(theme.palette.grey[50], 0.5),
                        mb: 2,
                        borderRadius: 1,
                        border: theme.palette.mode === 'dark'
                          ? `1px solid ${alpha(theme.palette.divider, 0.25)}`
                          : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 4px 12px rgba(0,0,0,0.4)'
                          : '0 2px 8px rgba(0,0,0,0.06)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha('#222', 0.85)
                            : alpha(theme.palette.grey[50], 0.7),
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 6px 16px rgba(0,0,0,0.5)'
                            : '0 4px 12px rgba(0,0,0,0.08)'
                        }
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 2.5,
                          pb: 1.5,
                          borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
                        }}>
                          <Box sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.2)}, ${alpha(theme.palette.info.main, 0.1)})`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 1.5
                          }}>
                            <PhysicalIcon sx={{ color: 'info.main', fontSize: 20 }} />
                          </Box>
                          <Typography variant="h6" sx={{ 
                            fontWeight: 700,
                            letterSpacing: '-0.3px'
                          }}>
                            Physical Description
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ 
                          lineHeight: 1.7,
                          color: theme.palette.mode === 'dark' ? alpha('#fff', 0.9) : 'text.primary'
                        }}>
                          {character.physicalDescription}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  
                  {/* Personality */}
                  {character.personality && (
                    <Grid item xs={12}>
                      <Paper elevation={0} sx={{ 
                        p: 2.5, 
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha('#1a1a1a', 0.8)
                          : alpha(theme.palette.grey[50], 0.5),
                        mb: 2,
                        borderRadius: 1,
                        border: theme.palette.mode === 'dark'
                          ? `1px solid ${alpha(theme.palette.divider, 0.25)}`
                          : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 4px 12px rgba(0,0,0,0.4)'
                          : '0 2px 8px rgba(0,0,0,0.06)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha('#222', 0.85)
                            : alpha(theme.palette.grey[50], 0.7),
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 6px 16px rgba(0,0,0,0.5)'
                            : '0 4px 12px rgba(0,0,0,0.08)'
                        }
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Box sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.2)}, ${alpha(theme.palette.info.main, 0.1)})`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 1.25
                          }}>
                            <PersonalityIcon sx={{ color: 'info.main', fontSize: 20 }} />
                          </Box>
                          <Box>
                            <Typography variant="overline" sx={{ letterSpacing: 0.6, fontWeight: 700, color: 'text.secondary' }}>
                              Personality
                            </Typography>
                            {character.personalityType && (
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {getPersonalityLabel(character.personalityType)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Typography variant="body1" sx={{ lineHeight: 1.7, color: theme.palette.mode === 'dark' ? alpha('#fff', 0.9) : 'text.primary' }}>
                          {character.personality}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* Goals & Motivation */}
                  {(character.goals || character.motivation) && (
                    <Grid item xs={12}>
                      <Paper elevation={0} sx={{ 
                        p: 2.5, 
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha('#1a1a1a', 0.8)
                          : alpha(theme.palette.grey[50], 0.5),
                        mb: 2,
                        borderRadius: 1,
                        border: theme.palette.mode === 'dark'
                          ? `1px solid ${alpha(theme.palette.divider, 0.25)}`
                          : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 4px 12px rgba(0,0,0,0.4)'
                          : '0 2px 8px rgba(0,0,0,0.06)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha('#222', 0.85)
                            : alpha(theme.palette.grey[50], 0.7),
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 6px 16px rgba(0,0,0,0.5)'
                            : '0 4px 12px rgba(0,0,0,0.08)'
                        }
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 2.5,
                          pb: 1.5,
                          borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
                        }}>
                          <Box sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.2)}, ${alpha(theme.palette.warning.main, 0.1)})`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 1.5
                          }}>
                            <GoalsIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                          </Box>
                          <Typography variant="h6" sx={{ 
                            fontWeight: 700,
                            letterSpacing: '-0.3px'
                          }}>
                            Goals & Motivation
                          </Typography>
                        </Box>
                        {character.goals && (
                          <Box sx={{ mb: 2.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ 
                              mb: 1, 
                              display: 'block', 
                              textTransform: 'uppercase', 
                              letterSpacing: 0.5, 
                              fontWeight: 600 
                            }}>
                              Goals
                            </Typography>
                            <Typography variant="body1" sx={{ 
                              lineHeight: 1.7,
                              color: theme.palette.mode === 'dark' ? alpha('#fff', 0.9) : 'text.primary'
                            }}>
                              {character.goals}
                            </Typography>
                          </Box>
                        )}
                        {character.motivation && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ 
                              mb: 1, 
                              display: 'block', 
                              textTransform: 'uppercase', 
                              letterSpacing: 0.5, 
                              fontWeight: 600 
                            }}>
                              Motivation
                            </Typography>
                            <Typography variant="body1" sx={{ 
                              lineHeight: 1.7,
                              color: theme.palette.mode === 'dark' ? alpha('#fff', 0.9) : 'text.primary'
                            }}>
                              {character.motivation}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  )}
                  
                  {/* Conflicts */}
                  {(character.internalConflict || character.externalConflict) && (
                    <Grid item xs={12}>
                      <Paper elevation={0} sx={{ 
                        p: 2.5, 
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha('#1a1a1a', 0.8)
                          : alpha(theme.palette.grey[50], 0.5),
                        mb: 2,
                        borderRadius: 1,
                        border: theme.palette.mode === 'dark'
                          ? `1px solid ${alpha(theme.palette.divider, 0.25)}`
                          : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 4px 12px rgba(0,0,0,0.4)'
                          : '0 2px 8px rgba(0,0,0,0.06)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha('#222', 0.85)
                            : alpha(theme.palette.grey[50], 0.7),
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 6px 16px rgba(0,0,0,0.5)'
                            : '0 4px 12px rgba(0,0,0,0.08)'
                        }
                      }}>
                        <Typography variant="overline" gutterBottom sx={{ 
                          fontWeight: 700,
                          letterSpacing: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <ConflictIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                          Conflicts
                        </Typography>
                        {character.internalConflict && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Internal Conflict
                              {character.internalConflictType && (
                                <Chip 
                                  label={character.internalConflictType.replace(/-/g, ' ')} 
                                  size="small" 
                                  color="default" 
                                  sx={{ ml: 1 }} 
                                />
                              )}
                            </Typography>
                            <Typography variant="body1">
                              {character.internalConflict}
                            </Typography>
                          </Box>
                        )}
                        {character.externalConflict && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              External Conflict
                              {character.externalConflictType && (
                                <Chip 
                                  label={character.externalConflictType.replace(/-/g, ' ')} 
                                  size="small" 
                                  color="default" 
                                  sx={{ ml: 1 }} 
                                />
                              )}
                            </Typography>
                            <Typography variant="body1">
                              {character.externalConflict}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  )}
                  
                  {/* Background */}
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ 
                      p: 2.5, 
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha('#1a1a1a', 0.8)
                        : alpha(theme.palette.grey[50], 0.5),
                      mb: 2,
                      borderRadius: 1,
                      border: theme.palette.mode === 'dark'
                        ? `1px solid ${alpha(theme.palette.divider, 0.25)}`
                        : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 12px rgba(0,0,0,0.4)'
                        : '0 2px 8px rgba(0,0,0,0.06)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha('#222', 0.85)
                          : alpha(theme.palette.grey[50], 0.7),
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 6px 16px rgba(0,0,0,0.5)'
                          : '0 4px 12px rgba(0,0,0,0.08)'
                      }
                    }}>
                      <Typography variant="overline" gutterBottom sx={{ 
                        fontWeight: 700,
                        letterSpacing: 1,
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                        Background
                      </Typography>
                      {character.background ? (
                        <Typography variant="body1">
                          {character.background}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No background information has been provided for this character.
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                  
                  {/* Character Arc */}
                  {character.arc && (
                    <Grid item xs={12}>
                      <Paper elevation={0} sx={{ 
                        p: 2.5, 
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha('#1a1a1a', 0.8)
                          : alpha(theme.palette.grey[50], 0.5),
                        mb: 2,
                        borderRadius: 1,
                        border: theme.palette.mode === 'dark'
                          ? `1px solid ${alpha(theme.palette.divider, 0.25)}`
                          : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 4px 12px rgba(0,0,0,0.4)'
                          : '0 2px 8px rgba(0,0,0,0.06)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha('#222', 0.85)
                            : alpha(theme.palette.grey[50], 0.7),
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 6px 16px rgba(0,0,0,0.5)'
                            : '0 4px 12px rgba(0,0,0,0.08)'
                        }
                      }}>
                        <Typography variant="overline" gutterBottom sx={{ 
                          fontWeight: 700,
                          letterSpacing: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <ArcIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                          Character Arc
                        </Typography>
                        <Typography variant="body1">
                          {character.arc}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  
                  {/* Voice */}
                  {character.voice && (
                    <Grid item xs={12}>
                      <Paper elevation={0} sx={{ 
                        p: 2.5, 
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha('#1a1a1a', 0.8)
                          : alpha(theme.palette.grey[50], 0.5),
                        mb: 2,
                        borderRadius: 1,
                        border: theme.palette.mode === 'dark'
                          ? `1px solid ${alpha(theme.palette.divider, 0.25)}`
                          : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 4px 12px rgba(0,0,0,0.4)'
                          : '0 2px 8px rgba(0,0,0,0.06)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha('#222', 0.85)
                            : alpha(theme.palette.grey[50], 0.7),
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 6px 16px rgba(0,0,0,0.5)'
                            : '0 4px 12px rgba(0,0,0,0.08)'
                        }
                      }}>
                        <Typography variant="overline" gutterBottom sx={{ 
                          fontWeight: 700,
                          letterSpacing: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <VoiceIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                          Voice/Speech Characteristics
                        </Typography>
                        <Typography variant="body1">
                          {character.voice}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  
                  {/* Relationships */}
                  {characterRelationships.length > 0 && (
                    <Grid item xs={12}>
                      <Paper elevation={0} sx={{ 
                        p: 2.5, 
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha('#1a1a1a', 0.8)
                          : alpha(theme.palette.grey[50], 0.5),
                        mb: 2,
                        borderRadius: 1,
                        border: theme.palette.mode === 'dark'
                          ? `1px solid ${alpha(theme.palette.divider, 0.25)}`
                          : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 4px 12px rgba(0,0,0,0.4)'
                          : '0 2px 8px rgba(0,0,0,0.06)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha('#222', 0.85)
                            : alpha(theme.palette.grey[50], 0.7),
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 6px 16px rgba(0,0,0,0.5)'
                            : '0 4px 12px rgba(0,0,0,0.08)'
                        }
                      }}>
                        <Typography variant="overline" gutterBottom sx={{ 
                          fontWeight: 700,
                          letterSpacing: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <RelationshipsIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                          Relationships
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {characterRelationships.map((rel, index) => {
                            const isSource = rel.sourceId === character.id;
                            const otherCharacterId = isSource ? rel.targetId : rel.sourceId;
                            const relationshipLabel = getRelationshipTypeLabel(rel.type);
                            const otherCharacterName = getCharacterNameById(otherCharacterId);
                            
                            return (
                              <Paper 
                                key={index} 
                                elevation={0} 
                                sx={{ 
                                  p: 1.5, 
                                  borderRadius: 2,
                                  border: theme.palette.mode === 'dark'
                                    ? `1px solid ${alpha(theme.palette.divider, 0.2)}`
                                    : '1px solid rgba(0,0,0,0.08)',
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? alpha('#0a0a0a', 0.4)
                                    : 'transparent',
                                  display: 'flex', 
                                  alignItems: 'center',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                      ? alpha('#111', 0.5)
                                      : 'rgba(0,0,0,0.02)',
                                    borderColor: theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.primary.main, 0.3)
                                      : 'rgba(0,0,0,0.12)',
                                    transform: 'translateX(4px)'
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                    {isSource ? (
                                      <>
                                        <span>{character.name} is </span>
                                        <Tooltip title={rel.description || `${character.name} is ${relationshipLabel.toLowerCase()} to ${otherCharacterName}`}>
                                          <Chip 
                                            label={relationshipLabel} 
                                            size="small" 
                                            color={
                                              rel.type === 'friend' || rel.type === 'lover' ? 'success' :
                                              rel.type === 'enemy' || rel.type === 'rival' ? 'error' : 'primary'
                                            }
                                            sx={{ mx: 0.5 }} 
                                          />
                                        </Tooltip>
                                        <span> to </span>
                                        <strong>{otherCharacterName}</strong>
                                      </>
                                    ) : (
                                      <>
                                        <strong>{otherCharacterName}</strong>
                                        <span> is </span>
                                        <Tooltip title={rel.description || `${otherCharacterName} is ${relationshipLabel.toLowerCase()} to ${character.name}`}>
                                          <Chip 
                                            label={relationshipLabel} 
                                            size="small" 
                                            color={
                                              rel.type === 'friend' || rel.type === 'lover' ? 'success' :
                                              rel.type === 'enemy' || rel.type === 'rival' ? 'error' : 'primary'
                                            }
                                            sx={{ mx: 0.5 }} 
                                          />
                                        </Tooltip>
                                        <span> to </span>
                                        <span>{character.name}</span>
                                      </>
                                    )}
                                  </Typography>
                                </Box>
                              </Paper>
                            );
                          })}
                        </Box>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
            <Tooltip title="Edit this character's details">
              <Button 
                onClick={onEdit}
                startIcon={<EditIcon />}
                variant="contained"
                color="primary"
              >
                Edit Character
              </Button>
            </Tooltip>
            <Button 
              onClick={onClose}
              variant="outlined"
              sx={{ ml: 1 }}
            >
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default CharacterDetailView;
